/**
 * PunchListRail
 * ──────────────────────────────────────────────────────────────────────
 * Compact right-rail version of the AI punch list (Edit 1 of
 * scheduler_edits_v2.md).
 *
 * Width rules (per spec):
 *   - Expanded state: 260px max — shows color dot + patient/time + short
 *                     label + 1 action button (max 2 words).
 *   - Collapsed state: 40px icon-only rail of colored dots.
 *
 * No financial literals in this collapsed view — the full card with
 * financial context is still available by tapping an item (bubbled up
 * to the parent via `onExpand(item)` if provided) or via the parent's
 * inline expansion.
 *
 * No banned literals live in this file — it renders purely from the
 * `items` prop supplied by the caller (demo fixtures OR real API data).
 */
import { useState } from 'react';
import { cn } from '../../../../lib/utils';
import { Button } from '../../../ui/button';
import {
  ChevronLeft, ChevronRight, CheckCircle2, Phone, ScanLine, Sparkles,
  DollarSign, Zap, Loader2, AlertTriangle, RefreshCw,
} from 'lucide-react';

const PRIORITY_DOT = {
  red: 'bg-red-500',
  amber: 'bg-amber-500',
  blue: 'bg-blue-500',
  green: 'bg-emerald-500',
};
const PRIORITY_BG = {
  red: 'bg-red-50 border-red-100',
  amber: 'bg-amber-50 border-amber-100',
  blue: 'bg-blue-50 border-blue-100',
  green: 'bg-emerald-50 border-emerald-100',
};
const ACTION_ICON = {
  call: Phone,
  scan: ScanLine,
  autofill: Sparkles,
  collect: DollarSign,
  book: Sparkles,
};

const shortAction = (label) => {
  if (!label) return null;
  // Cap to max 2 words as required by spec ("Call," "Fill," "Collect," "Book")
  const parts = label.split(/\s+/).slice(0, 2);
  return parts.join(' ');
};

const PunchListRail = ({
  items = [],
  loading = false,
  error = null,
  onRetry,
  onAction,
  onExpand,
  initialCollapsed = false,
  testId = 'punch-list-rail',
}) => {
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  const [expandedId, setExpandedId] = useState(null);

  if (collapsed) {
    return (
      <div
        className="shrink-0 rounded-lg border border-slate-200 bg-white flex flex-col items-center py-2 gap-2"
        style={{ width: 40 }}
        data-testid={`${testId}-collapsed`}
      >
        <button
          onClick={() => setCollapsed(false)}
          className="p-1 rounded-md hover:bg-slate-100"
          style={{ border: 'none', cursor: 'pointer' }}
          aria-label="Expand punch list"
          data-testid={`${testId}-expand-btn`}
        >
          <ChevronLeft className="h-3 w-3 text-slate-500" />
        </button>
        <Zap className="h-3.5 w-3.5 text-violet-500" />
        <div className="flex flex-col items-center gap-1.5 mt-1">
          {items.map((it) => (
            <div
              key={it.id}
              className={cn('h-2 w-2 rounded-full', PRIORITY_DOT[it.priority] || 'bg-slate-300')}
              title={it.patient || it.time || ''}
              data-testid={`${testId}-dot-${it.id}`}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <aside
      className="shrink-0 rounded-lg border border-slate-200 bg-white flex flex-col"
      style={{ width: 260 }}
      data-testid={testId}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
        <div className="flex items-center gap-1.5 min-w-0">
          <Zap className="h-3.5 w-3.5 text-violet-500 shrink-0" />
          <span className="text-[11px] font-semibold text-slate-800 truncate">Punch list</span>
          <span className="text-[9px] text-slate-400 shrink-0">({items.length})</span>
        </div>
        <button
          onClick={() => setCollapsed(true)}
          className="p-1 rounded-md hover:bg-slate-100"
          style={{ border: 'none', cursor: 'pointer' }}
          aria-label="Collapse punch list"
          data-testid={`${testId}-collapse-btn`}
        >
          <ChevronRight className="h-3 w-3 text-slate-500" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
        {loading && (
          <div className="flex items-center justify-center py-6" data-testid={`${testId}-loading`}>
            <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
            <span className="ml-2 text-[10px] text-slate-500">Loading…</span>
          </div>
        )}
        {!loading && error && (
          <div
            className="p-2 rounded-md bg-red-50 border border-red-100"
            data-testid={`${testId}-error`}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <AlertTriangle className="h-3 w-3 text-red-500 shrink-0" />
              <span className="text-[9px] text-red-700 truncate">{error?.message || 'Failed'}</span>
            </div>
            {onRetry && (
              <button
                onClick={onRetry}
                className="text-[9px] text-red-700 underline inline-flex items-center gap-1"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                data-testid={`${testId}-retry`}
              >
                <RefreshCw className="h-2.5 w-2.5" /> Retry
              </button>
            )}
          </div>
        )}
        {!loading && !error && items.length === 0 && (
          <div className="py-6 text-center" data-testid={`${testId}-empty`}>
            <CheckCircle2 className="h-4 w-4 text-emerald-300 mx-auto mb-1" />
            <p className="text-[10px] text-slate-500">All clear.</p>
          </div>
        )}
        {!loading && !error && items.map((item) => {
          const Icon = ACTION_ICON[item.actionType];
          const isExpanded = expandedId === item.id;
          return (
            <div
              key={item.id}
              className={cn(
                'rounded-md border transition-colors',
                PRIORITY_BG[item.priority] || 'bg-slate-50 border-slate-100'
              )}
              data-testid={`${testId}-item-${item.id}`}
            >
              <button
                onClick={() => {
                  const next = isExpanded ? null : item.id;
                  setExpandedId(next);
                  if (!isExpanded && onExpand) onExpand(item);
                }}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-left"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                data-testid={`${testId}-toggle-${item.id}`}
              >
                <div
                  className={cn(
                    'h-2 w-2 rounded-full shrink-0',
                    PRIORITY_DOT[item.priority] || 'bg-slate-300'
                  )}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-medium text-slate-800 truncate">
                    {item.patient || item.time || '—'}
                  </div>
                  <div className="text-[9px] text-slate-500 truncate">{item.issue}</div>
                </div>
                {item.action && (
                  <span
                    className="text-[9px] font-semibold text-slate-700 shrink-0"
                    aria-hidden
                  >
                    {shortAction(item.action)}
                  </span>
                )}
              </button>
              {isExpanded && (
                <div
                  className="px-2 pb-2 pt-1 border-t border-slate-200/60 space-y-1.5"
                  data-testid={`${testId}-detail-${item.id}`}
                >
                  {item.time && item.patient && (
                    <div className="text-[9px] text-slate-500">{item.time}</div>
                  )}
                  <div className="text-[10px] text-slate-700">{item.issue}</div>
                  {item.financial && (
                    <div className="text-[9px] text-slate-500 inline-flex items-center gap-1">
                      <DollarSign className="h-2.5 w-2.5" />
                      {item.financial}
                    </div>
                  )}
                  {item.echoHandling && (
                    <div className="text-[9px] text-blue-600 font-medium">{item.echoHandling}</div>
                  )}
                  {item.action && (
                    <Button
                      size="sm"
                      className={cn(
                        'h-6 w-full text-[10px] gap-1',
                        item.actionType === 'autofill'
                          ? 'bg-[#6b5ce7] hover:bg-[#5a4bd6] text-white'
                          : 'bg-[#111] hover:bg-[#222] text-white'
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onAction) onAction(item);
                      }}
                      data-testid={`${testId}-action-${item.id}`}
                    >
                      {Icon && <Icon className="h-2.5 w-2.5" />}
                      {shortAction(item.action)}
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
};

export default PunchListRail;
